import { useEffect, useState } from 'react';
import { Button, InputNumber, Space, Tooltip, Typography, message } from 'antd';
import { AimOutlined, VerticalAlignBottomOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import useSceneStore from '../../stores/sceneStore';
import { modelRefsRegistry } from '../../stores/modelRefsRegistry';
import type { Model3D } from '@dtwin/shared';
import { round3 } from '../../utils/pivot';

const { Text } = Typography;

// ============================================
// 模型轴心修正（2026-10-07）：第三方模型（FBX/GLB）视觉中心常不在原点，
// 拖动/旋转手柄（挂在原点）会飘在模型外。这里可一键把轴心归位到模型中心
// （模型视觉位置不变），或手动填写修正值微调；随场景保存/预览/发布生效。
// ============================================

export default function PivotFixCard({ model }: { model: Model3D }) {
  const offset = useSceneStore((s) => s.pivotOffsets?.[model.id]) as [number, number, number] | undefined;
  const updateModel = useSceneStore((s) => s.updateModel);
  const setPivotOffset = useSceneStore((s) => s.setPivotOffset);
  const [x, setX] = useState(0);
  const [y, setY] = useState(0);
  const [z, setZ] = useState(0);

  useEffect(() => {
    setX(offset?.[0] ?? 0);
    setY(offset?.[1] ?? 0);
    setZ(offset?.[2] ?? 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model.id, offset?.[0], offset?.[1], offset?.[2]]);

  const auto = (mode: 'ground' | 'center') => {
    const handle = modelRefsRegistry.get(model.id);
    const r = handle?.applyAutoPivot?.(mode);
    if (!r) {
      message.warning('模型尚未加载完成，请稍后再试');
      return;
    }
    setPivotOffset(model.id, r.offset);
    updateModel(model.id, { position: r.position });
    message.success(mode === 'ground' ? '轴心已移到模型底面中心（手柄对齐模型，视觉位置不变）' : '轴心已移到模型几何中心（视觉位置不变）');
  };

  const applyManual = () => {
    setPivotOffset(model.id, [round3(x), round3(y), round3(z)]);
    message.success('已应用自定义修正值（Ctrl+S 保存后持久）');
  };

  const reset = () => {
    setPivotOffset(model.id, null);
    message.success('已复位轴心修正（恢复自动贴地）');
  };

  const toEditorCenter = () => {
    updateModel(model.id, { position: [0, model.position[1], 0] });
    message.success('模型已水平移到编辑器中心');
  };

  return (
    <div data-testid="pivot-fix-card" style={{ border: '1px solid #30475e', borderRadius: 6, padding: '8px 10px', background: '#16162a' }}>
      <Text strong style={{ fontSize: 12 }}>
        轴心修正{' '}
        <Tooltip
          title={
            <div style={{ fontSize: 12, lineHeight: 1.7 }}>
              第三方模型（FBX/GLB）的视觉中心常不在原点：拖动/旋转手柄会飘在模型外。<br />
              · <b>轴心→底面中心</b>：水平居中 + 贴地（多数场景推荐）；<br />
              · <b>轴心→几何中心</b>：含高度居中；<br />
              · 修正后模型视觉位置不变，仅手柄/旋转中心归位；可手动填写修正值微调；随场景保存，预览/发布生效。
            </div>
          }
        >
          <QuestionCircleOutlined style={{ color: '#666', cursor: 'help' }} />
        </Tooltip>
      </Text>
      <div style={{ fontSize: 11, color: '#777', margin: '4px 0 8px' }}>
        手柄应在模型中心；偏移时点下面按钮一键修正（视觉位置不变）
      </div>
      <Space wrap size={6}>
        <Button size="small" icon={<VerticalAlignBottomOutlined />} data-testid="pivot-auto-ground" onClick={() => auto('ground')}>
          轴心→底面中心（贴地）
        </Button>
        <Button size="small" icon={<AimOutlined />} data-testid="pivot-auto-center" onClick={() => auto('center')}>
          轴心→几何中心
        </Button>
        <Button size="small" data-testid="pivot-to-editor-center" onClick={toEditorCenter}>
          移到编辑器中心
        </Button>
        <Button size="small" type="text" data-testid="pivot-reset" onClick={reset} disabled={!offset}>
          复位
        </Button>
      </Space>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
        <span style={{ fontSize: 12, color: '#888', flexShrink: 0 }}>修正值 (m)</span>
        <InputNumber size="small" id="pivot-manual-x" value={x} onChange={(v) => setX(Number(v) || 0)} step={0.1} style={{ width: 74 }} data-testid="pivot-manual-x" />
        <InputNumber size="small" id="pivot-manual-y" value={y} onChange={(v) => setY(Number(v) || 0)} step={0.1} style={{ width: 74 }} data-testid="pivot-manual-y" />
        <InputNumber size="small" id="pivot-manual-z" value={z} onChange={(v) => setZ(Number(v) || 0)} step={0.1} style={{ width: 74 }} data-testid="pivot-manual-z" />
        <Button size="small" type="primary" data-testid="pivot-manual-apply" onClick={applyManual}>
          应用
        </Button>
      </div>
    </div>
  );
}
